import { eventId } from "@shared/analytics/walletEvents";
import { type ApplicationDraft, DEFAULT_DRAFT } from "@shared/widgets/yaas/types";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import {
    type ParticipantAccountStatus,
    type RetailEnrollmentAccount,
    type RetailVerificationMethod,
    useWallet,
    useWalletModal
} from "@solomon-apps/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import {
    clearPendingEnrollment,
    readPendingEnrollment,
    savePendingEnrollment
} from "@/hooks/yaas/pendingEnrollmentVerification";
import { useRetailAccount } from "@/hooks/yaas/useRetailAccount";
import { RetailEnrollmentRequestError, useRetailEnrollment } from "@/hooks/yaas/useRetailEnrollment";
import { useWalletVerification, WalletVerificationError } from "@/hooks/yaas/useWalletVerification";
import { EnrollmentJourney } from "@/services/analytics/enrollment";
import { normalizeVerificationTransfer, VERIFICATION_TRANSFER_SOL } from "@/services/yaas/verificationTransfer";

import {
    accountStatusFromEnrollmentError,
    enrollmentFailureAction,
    type EnrollmentFlowFailure,
    toFlowFailure
} from "./retailEnrollmentFailure";

export type PageView = "disconnected" | "not-enrolled" | "enrolling" | "rewards-enabled" | "account-unavailable";

export type FormPhase = "idle" | "verifying" | "verified" | "submitting" | "submitted" | "error";

export type SimMode = "enroll" | "connect" | "full" | "short";
/**
 * `hardware`: a wallet that cannot sign an offchain message (a Ledger, say).
 * The signature fails, the form offers a small transfer, and the transfer
 * goes through the real consent modal before it "approves".
 */
export type SimErrorTrigger = "sign-rejected" | "backend" | "hardware" | "hardware-cancel";

export interface UseYaasRetailFlowResult {
    analytics?: EnrollmentJourney;
    pageView: PageView;
    formPhase: FormPhase;
    verificationMethod: RetailVerificationMethod | null;
    /** The wallet cannot sign messages, so the attempt carries on as a
     *  small transfer without stopping on a failure. True from the
     *  signing failure until that attempt resolves. */
    signingFallback: boolean;
    formFailure: EnrollmentFlowFailure | null;
    successfulAccount: RetailEnrollmentAccount | null;
    unavailableAccountStatus: Exclude<ParticipantAccountStatus, "Active"> | null;
    termsResetKey: number;
    walletAddress: string;
    isSquads: boolean;
    vaultAddress: string;
    isAccountReady: boolean;
    walletProviderName: string | null;
    isWalletConnected: boolean;
    isWalletConnecting: boolean;
    isSimulated: boolean;
    simMode: SimMode | null;
    connectWallet: () => void;
    startEnrollment: () => void;
    cancelEnrollment: () => void;
    /** Drop the current failure and return the form to its start. */
    clearFailure: () => void;
    submitApplication: (
        draft: ApplicationDraft,
        preferredMethod?: "microTransfer",
        restartVerification?: boolean
    ) => Promise<void>;
    isSubmittingApplication: boolean;
}

interface SimTimings {
    connecting: number;
    verifying: number;
    verified: number;
    submitting: number;
}

const DEFAULT_TIMINGS: SimTimings = {
    connecting: 1200,
    verifying: 1500,
    verified: 800,
    submitting: 2000
};
const REAL_VERIFIED_DWELL_MS = 600;
// How long "Signing Not Available, Trying Transfer…" holds before the transfer
// consent opens over it: long enough to read, so the modal arrives explained.
const SIGNING_FALLBACK_DWELL_MS = 1500;
// How long "Ownership Verified" holds on the button before the page moves on
// to the enrolled view: enough for the light-bar's success beat to land.
const SUCCESS_DWELL_MS = 1200;

/** Wallets that cannot sign an offchain message at all (hardware wallets,
 *  mostly). A rejection or a timeout is the user's or the wallet's answer and
 *  still stops on a failure; these two mean signing was never an option. */
function signingUnavailable(error: unknown): boolean {
    return (
        error instanceof WalletVerificationError &&
        error.method === "signMessage" &&
        (error.code === "signing_failed" || error.code === "unsupported_signing")
    );
}
const AUTOSTART_DELAY_AFTER_CONNECT = 800;
const AUTOSTART_DELAY_BEFORE_SUBMIT = 600;
// 44 characters: the 45-character mock used elsewhere decodes to 33 bytes,
// fails the form's address shape check, and leaves the sim's submit disabled.
const SIM_WALLET_ADDRESS = "EnViUgEjqL6HzLRxLDnxTb4t5xukumJYpNwq6fxF1tJY";
const SIM_WALLET_PROVIDER = "Phantom";

const SIM_ENROLLMENT_ACCOUNT: RetailEnrollmentAccount = {
    uuid: "11111111-1111-4111-8111-111111111111",
    address: SIM_WALLET_ADDRESS,
    rewardsDestinationAddress: SIM_WALLET_ADDRESS,
    distributionMode: "stream",
    status: "Active",
    isRewardsEligible: true,
    totalPositionsValue: "0",
    totalEarnedRewards: "0",
    createdAt: "2026-03-09T00:00:00.000Z",
    updatedAt: "2026-03-09T00:00:00.000Z"
};

interface SimConfig {
    mode: SimMode;
    speed: number;
    errorTrigger: SimErrorTrigger | null;
    autostart: boolean;
}

interface SimWalletState {
    connected: boolean;
    connecting: boolean;
    address: string;
    providerName: string;
}

function readSimConfig(): SimConfig | null {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("simulate");
    if (mode !== "enroll" && mode !== "connect" && mode !== "full" && mode !== "short") {
        return null;
    }
    const speedValue = params.get("speed")?.match(/^(\d+(?:\.\d+)?)x?$/)?.[1];
    const parsedSpeed = speedValue ? Number(speedValue) : 1;
    const error = params.get("error");
    return {
        mode,
        speed: Number.isFinite(parsedSpeed) && parsedSpeed > 0 ? parsedSpeed : 1,
        errorTrigger:
            error === "sign-rejected" || error === "backend" || error === "hardware" || error === "hardware-cancel"
                ? error
                : null,
        autostart: params.get("autostart") === "1"
    };
}

function timingsFor(speed: number): SimTimings {
    return {
        connecting: DEFAULT_TIMINGS.connecting / speed,
        verifying: DEFAULT_TIMINGS.verifying / speed,
        verified: DEFAULT_TIMINGS.verified / speed,
        submitting: DEFAULT_TIMINGS.submitting / speed
    };
}

export function useYaasRetailFlow(): UseYaasRetailFlowResult {
    const [simConfig] = useState(readSimConfig);
    const isSimulated = simConfig !== null;
    const realWallet = useWallet();
    const selectedAccount = useRetailAccount();
    const walletModal = useWalletModal();
    const enroll = useRetailEnrollment(selectedAccount.isSquads ? selectedAccount.signerAddress : undefined);
    const { verify, verificationMethod } = useWalletVerification();

    const startsConnected = simConfig?.mode === "enroll" || simConfig?.mode === "short";
    const [simWallet, setSimWallet] = useState<SimWalletState>(() => ({
        connected: startsConnected,
        connecting: false,
        address: startsConnected ? SIM_WALLET_ADDRESS : "",
        providerName: startsConnected ? SIM_WALLET_PROVIDER : ""
    }));
    const [formPhase, setFormPhase] = useState<FormPhase>("idle");
    const [simVerificationMethod, setSimVerificationMethod] = useState<RetailVerificationMethod | null>(null);
    const [signingFallback, setSigningFallback] = useState(false);
    const [formFailure, setFormFailure] = useState<EnrollmentFlowFailure | null>(null);
    const [successfulAccount, setSuccessfulAccount] = useState<RetailEnrollmentAccount | null>(null);
    const [unavailableAccountStatus, setUnavailableAccountStatus] = useState<Exclude<
        ParticipantAccountStatus,
        "Active"
    > | null>(null);
    const [isEnrolling, setIsEnrolling] = useState(simConfig?.mode === "short");
    const [termsResetKey, setTermsResetKey] = useState(0);

    const timersRef = useRef<number[]>([]);
    const attemptIdRef = useRef(0);
    const submittingRef = useRef(false);
    const verificationAbortRef = useRef<AbortController | null>(null);
    const preferredMethodRef = useRef<"microTransfer" | undefined>(undefined);
    const submitRef = useRef<
        (draft: ApplicationDraft, preferredMethod?: "microTransfer", restartVerification?: boolean) => Promise<void>
    >(async () => undefined);

    const clearTimers = useCallback((): void => {
        timersRef.current.forEach((id) => window.clearTimeout(id));
        timersRef.current = [];
    }, []);

    const isWalletConnected = isSimulated ? simWallet.connected : realWallet.connected;
    const isWalletConnecting = isSimulated ? simWallet.connecting : realWallet.connecting;
    const walletAddress = isSimulated ? simWallet.address : selectedAccount.address;
    const walletProviderName = isSimulated ? simWallet.providerName || null : realWallet.walletName;
    const walletSessionKey = isWalletConnected ? `${walletAddress}:${selectedAccount.signerAddress}` : "";
    const walletSessionKeyRef = useRef(walletSessionKey);

    const analyticsRef = useRef<EnrollmentJourney | undefined>(undefined);
    const currentSuccess = successfulAccount?.address === walletAddress ? successfulAccount : null;
    const pageView: PageView = !isWalletConnected
        ? "disconnected"
        : unavailableAccountStatus
          ? "account-unavailable"
          : currentSuccess
            ? "rewards-enabled"
            : isEnrolling
              ? "enrolling"
              : "not-enrolled";

    const cancelEnrollment = useCallback((): void => {
        analyticsRef.current?.close("back");
        clearTimers();
        verificationAbortRef.current?.abort();
        attemptIdRef.current += 1;
        submittingRef.current = false;
        preferredMethodRef.current = undefined;
        setSigningFallback(false);
        setFormFailure(null);
        setFormPhase("idle");
        setIsEnrolling(false);
    }, [clearTimers]);

    useEffect(() => {
        if (walletSessionKeyRef.current === walletSessionKey) return;
        analyticsRef.current?.close(walletSessionKey ? "account_change" : "disconnect");
        analyticsRef.current = undefined;
        walletSessionKeyRef.current = walletSessionKey;
        cancelEnrollment();
        setSuccessfulAccount(null);
        setUnavailableAccountStatus(null);
    }, [cancelEnrollment, walletSessionKey]);

    useEffect(
        () => (): void => {
            analyticsRef.current?.close("route_change");
            clearTimers();
            verificationAbortRef.current?.abort();
            attemptIdRef.current += 1;
        },
        [clearTimers]
    );

    const connectWallet = useCallback((): void => {
        if (!isSimulated) {
            walletModal.openModal("retail_enrollment");
            return;
        }
        clearTimers();
        const timing = timingsFor(simConfig?.speed ?? 1);
        setSimWallet((current) => ({ ...current, connecting: true }));
        timersRef.current.push(
            window.setTimeout(() => {
                setSimWallet({
                    connected: true,
                    connecting: false,
                    address: SIM_WALLET_ADDRESS,
                    providerName: SIM_WALLET_PROVIDER
                });
            }, timing.connecting)
        );
    }, [clearTimers, isSimulated, simConfig?.speed, walletModal]);

    const clearFailure = useCallback((): void => {
        setSigningFallback(false);
        setFormFailure(null);
        setFormPhase("idle");
    }, []);

    const startEnrollment = useCallback((): void => {
        analyticsRef.current = new EnrollmentJourney(walletAddress, !isSimulated);
        analyticsRef.current.send("enroll:requested", { entry_point: "rewards_entry" });
        clearTimers();
        setSuccessfulAccount(null);
        setUnavailableAccountStatus(null);
        setFormFailure(null);
        setSigningFallback(false);
        setFormPhase("idle");
        setIsEnrolling(true);
    }, [clearTimers, walletAddress, isSimulated]);

    const applyFailure = useCallback((failure: EnrollmentFlowFailure): void => {
        const action = enrollmentFailureAction(failure.code);
        const unavailableStatus = accountStatusFromEnrollmentError(failure.code);
        setSigningFallback(false);
        setFormPhase("error");
        setFormFailure(failure);

        if (action === "account-unavailable" && unavailableStatus) {
            setUnavailableAccountStatus(unavailableStatus);
            setIsEnrolling(false);
            return;
        }
        if (action === "reset-terms") {
            setTermsResetKey((value) => value + 1);
            return;
        }
    }, []);

    const submitApplication = useCallback(
        async (
            draft: ApplicationDraft,
            preferredMethod?: "microTransfer",
            restartVerification = false
        ): Promise<void> => {
            if (submittingRef.current) return;
            clearTimers();
            setIsEnrolling(true);
            setFormFailure(null);
            if (preferredMethod !== "microTransfer") setSigningFallback(false);
            draft = { ...draft, terms: draft.terms ? Object.freeze({ ...draft.terms }) : undefined };
            preferredMethodRef.current = preferredMethod;

            if (isSimulated) {
                const timing = timingsFor(simConfig?.speed ?? 1);
                setSimVerificationMethod(preferredMethod ?? "signMessage");
                setFormPhase("verifying");
                const simHardware =
                    simConfig?.errorTrigger === "hardware" || simConfig?.errorTrigger === "hardware-cancel";
                if (simHardware) {
                    if (preferredMethod !== "microTransfer") {
                        // Signing is unavailable: the real path's fallback, on sim timers.
                        timersRef.current.push(
                            window.setTimeout(() => {
                                setSigningFallback(true);
                                timersRef.current.push(
                                    window.setTimeout(
                                        () => void submitRef.current(draft, "microTransfer"),
                                        SIGNING_FALLBACK_DWELL_MS / (simConfig?.speed ?? 1)
                                    )
                                );
                            }, timing.verifying)
                        );
                        return;
                    }
                    // The transfer leg goes straight to the wallet, as enrollment
                    // does. `hardware-cancel` declines it there; `hardware`
                    // approves and runs the ordinary success timers below.
                    if (simConfig?.errorTrigger === "hardware-cancel") {
                        timersRef.current.push(
                            window.setTimeout(() => {
                                const failure = toFlowFailure(
                                    new WalletVerificationError(
                                        "You canceled the verification transfer.",
                                        "wallet_rejected",
                                        "microTransfer"
                                    )
                                );
                                failure.canceled = true;
                                applyFailure(failure);
                            }, timing.verifying)
                        );
                        return;
                    }
                }
                if (simConfig?.errorTrigger === "sign-rejected") {
                    timersRef.current.push(
                        window.setTimeout(() => {
                            setFormPhase("error");
                            setFormFailure(
                                toFlowFailure(
                                    new WalletVerificationError("Signature request was rejected", "wallet_rejected")
                                )
                            );
                        }, timing.verifying)
                    );
                    return;
                }
                timersRef.current.push(
                    window.setTimeout(() => {
                        setFormPhase("verified");
                        timersRef.current.push(
                            window.setTimeout(() => {
                                setFormPhase("submitting");
                                timersRef.current.push(
                                    window.setTimeout(() => {
                                        if (simConfig?.errorTrigger === "backend") {
                                            setFormPhase("error");
                                            setFormFailure(
                                                toFlowFailure(
                                                    new RetailEnrollmentRequestError(
                                                        "Enrollment is unavailable",
                                                        "enrollment_unavailable",
                                                        503
                                                    )
                                                )
                                            );
                                            return;
                                        }
                                        setFormPhase("submitted");
                                        timersRef.current.push(
                                            window.setTimeout(
                                                () => {
                                                    setSuccessfulAccount({
                                                        ...SIM_ENROLLMENT_ACCOUNT,
                                                        rewardsDestinationAddress:
                                                            draft.rewardsDestination || SIM_WALLET_ADDRESS,
                                                        distributionMode: draft.distributionMode
                                                    });
                                                    setIsEnrolling(false);
                                                },
                                                SUCCESS_DWELL_MS / (simConfig?.speed ?? 1)
                                            )
                                        );
                                    }, timing.submitting)
                                );
                            }, timing.verified)
                        );
                    }, timing.verifying)
                );
                return;
            }

            submittingRef.current = true;
            verificationAbortRef.current?.abort();
            const controller = new AbortController();
            verificationAbortRef.current = controller;
            const attemptId = ++attemptIdRef.current;
            const journey = analyticsRef.current ?? new EnrollmentJourney(walletAddress, !isSimulated);
            analyticsRef.current = journey;
            const prior = restartVerification || selectedAccount.isSquads ? null : readPendingEnrollment(walletAddress);
            const restored = prior
                ? (prior.analytics ?? { flowId: journey.id, verificationId: eventId(), origin: "imported" as const })
                : undefined;
            const analytics = journey.attempt(prior?.draft ?? draft, restored);
            if (prior) savePendingEnrollment({ ...prior, analytics: analytics.record });
            const isCurrent = (): boolean => !controller.signal.aborted && attemptIdRef.current === attemptId;
            let failureStep = "preflight";
            analytics.phase = "preflight";
            let signedProofCanUseTransfer = false;
            let challengeHasTransfer = false;

            try {
                if (selectedAccount.isSquads && !selectedAccount.ready)
                    throw new Error("Recheck your Squads membership before enrolling.");
                if (selectedAccount.isSquads && preferredMethod === "microTransfer")
                    throw new Error("Squads enrollment requires a member to sign a message.");
                setFormPhase("verifying");
                if (restartVerification) clearPendingEnrollment(walletAddress);
                const pending = selectedAccount.isSquads ? null : readPendingEnrollment(walletAddress);
                // A broadcast proof stays bound to its original payload, even if the form was edited.
                const submittedDraft = pending?.draft ?? draft;
                const challenge =
                    pending?.challenge ??
                    (await enroll.requestChallenge(walletAddress, submittedDraft, controller.signal, () => {
                        failureStep = "challenge";
                        analytics.phase = "challenge";
                        analytics.send("enroll:challenge_started");
                    }));
                if (!pending)
                    analytics.send("enroll:challenge_completed", {
                        transfer_available: !selectedAccount.isSquads && !!challenge.microTransfer
                    });
                else if (pending.transactionSignature)
                    analytics.observe("enroll:verification_resumed", { proof_source: "journal" });
                const { nonce, message } = challenge;
                if (!isCurrent()) return;
                if (pending && Date.parse(challenge.expiresAt) <= Date.now()) {
                    throw new WalletVerificationError(
                        "The previous verification request expired",
                        "nonce_expired",
                        "microTransfer"
                    );
                }
                challengeHasTransfer =
                    !selectedAccount.isSquads &&
                    FEATURE_FLAGS.retailMicroTransferVerification &&
                    normalizeVerificationTransfer(challenge.microTransfer, challenge.nonce) !== null;
                const selectedMethod = pending ? "microTransfer" : preferredMethod;
                preferredMethodRef.current = selectedMethod;
                if (selectedMethod === "microTransfer" && !pending) {
                    savePendingEnrollment({
                        walletAddress,
                        draft: submittedDraft,
                        challenge,
                        analytics: analytics.record
                    });
                }
                failureStep = "wallet_sign";
                analytics.phase = "wallet_sign";
                const proof = pending?.transactionSignature
                    ? { method: "microTransfer" as const, transactionSignature: pending.transactionSignature }
                    : await verify({
                          analytics: analytics.observe,
                          purpose: "retail_enrollment",
                          walletAddress: selectedAccount.signerAddress || walletAddress,
                          message,
                          nonce,
                          expiresAt: challenge.expiresAt,
                          microTransfer: selectedAccount.isSquads ? undefined : challenge.microTransfer,
                          preferredMethod: selectedMethod,
                          signal: controller.signal,
                          onTransferBroadcast: (transactionSignature) =>
                              savePendingEnrollment({
                                  walletAddress,
                                  draft: submittedDraft,
                                  challenge,
                                  transactionSignature,
                                  analytics: analytics.record
                              })
                      });
                if (selectedAccount.isSquads && proof.method !== "signMessage")
                    throw new Error("Squads enrollment requires a member to sign a message.");
                if (proof.method === "microTransfer") {
                    savePendingEnrollment({
                        walletAddress,
                        draft: submittedDraft,
                        challenge,
                        transactionSignature: proof.transactionSignature,
                        analytics: analytics.record
                    });
                }
                if (!isCurrent()) return;
                signedProofCanUseTransfer = challengeHasTransfer && proof.method === "signMessage";
                setFormPhase("verified");
                await new Promise<void>((resolve) => {
                    const done = (): void => {
                        controller.signal.removeEventListener("abort", done);
                        resolve();
                    };
                    controller.signal.addEventListener("abort", done, { once: true });
                    timersRef.current.push(window.setTimeout(done, REAL_VERIFIED_DWELL_MS));
                });
                if (!isCurrent()) return;
                setFormPhase("submitting");
                failureStep = "submit";
                analytics.phase = "preflight";
                const result = await enroll.submitEnrollment(
                    walletAddress,
                    submittedDraft,
                    nonce,
                    proof,
                    isCurrent,
                    challenge.expiresAt,
                    controller.signal,
                    () => {
                        failureStep = "submit";
                        analytics.submitted(proof.method);
                    }
                );
                analytics.succeeded(result.created, result.account.isRewardsEligible);
                if (!isCurrent()) return;
                journey.close("completed");
                clearPendingEnrollment(walletAddress);
                // The form says "Ownership Verified" for a beat before the
                // enrolled view replaces it.
                setFormPhase("submitted");
                timersRef.current.push(
                    window.setTimeout(() => {
                        if (!isCurrent()) return;
                        setSuccessfulAccount(result.account);
                        setIsEnrolling(false);
                    }, SUCCESS_DWELL_MS)
                );
            } catch (error: unknown) {
                const failure = toFlowFailure(error);
                if (failureStep === "challenge")
                    analytics.send("enroll:challenge_failed", { error_code: failure.code });
                const uncertain =
                    (analytics.phase === "wallet_sign" && analytics.proofPending) ||
                    (analytics.phase === "submit" &&
                        ["network_error", "malformed_response", "verification_pending"].includes(failure.code));
                analytics.failed(
                    controller.signal.aborted ? "observation_aborted" : failure.code,
                    failure.status,
                    uncertain || (controller.signal.aborted && analytics.phase === "submit")
                );
                if (!isCurrent()) return;
                if (challengeHasTransfer && failureStep === "wallet_sign" && signingUnavailable(error)) {
                    // No failure to show: the transfer is the only way this
                    // wallet can verify, so the button stays pending and the
                    // attempt moves on to it. Consent is still asked for
                    // before any transfer is built.
                    journey.send("enroll:verification_method_selected", {
                        verification_method: "micro_transfer",
                        reason: "signing_unavailable"
                    });
                    setSigningFallback(true);
                    timersRef.current.push(
                        window.setTimeout(() => {
                            if (isCurrent()) void submitRef.current(draft, "microTransfer");
                        }, SIGNING_FALLBACK_DWELL_MS)
                    );
                    return;
                }
                if (failure.code === "rate_limited" && preferredMethodRef.current === "microTransfer") {
                    failure.retryMethod = "microTransfer";
                }
                if (signedProofCanUseTransfer && failureStep === "submit" && failure.code === "signature_invalid") {
                    failure.canRetryWithMicroTransfer = true;
                    failure.displayMessage = `Your wallet signature could not be verified. Try a small transfer (${VERIFICATION_TRANSFER_SOL} SOL plus network fee) instead.`;
                } else if (
                    challengeHasTransfer &&
                    failureStep === "wallet_sign" &&
                    error instanceof WalletVerificationError &&
                    error.method === "signMessage"
                ) {
                    failure.canRetryWithMicroTransfer = true;
                    if (failure.code === "wallet_rejected")
                        failure.displayMessage = "You canceled the signature request.";
                    failure.displayMessage += ` You can verify with a small transfer (${VERIFICATION_TRANSFER_SOL} SOL plus network fee) instead.`;
                }
                if (error instanceof WalletVerificationError && error.method === "microTransfer") {
                    if (error.code === "wallet_rejected") failure.canceled = true;
                    failure.canRetryWithMicroTransfer = challengeHasTransfer;
                    failure.retryMethod = "microTransfer";
                    // A declined or failed wallet request has no paid proof to recover.
                    if (
                        error.code === "wallet_rejected" ||
                        error.code === "transfer_failed" ||
                        error.code === "transfer_unavailable"
                    )
                        clearPendingEnrollment(walletAddress);
                }
                const pending = selectedAccount.isSquads ? null : readPendingEnrollment(walletAddress);
                if (pending) {
                    failure.hasTransferProof = !!pending.transactionSignature;
                    failure.retryWithNewChallenge = [
                        "nonce_expired",
                        "verification_invalid",
                        "payload_mismatch",
                        "terms_revision_mismatch"
                    ].includes(failure.code);
                    if (failure.hasTransferProof) {
                        failure.canRetryWithMicroTransfer = false;
                        failure.displayMessage = failure.retryWithNewChallenge
                            ? "The previous transfer could not complete verification. Starting a new verification may require another transfer and network fee."
                            : "Your verification transfer was sent. Check verification again without sending another transfer. Your original enrollment details will be used.";
                    }
                }
                applyFailure(failure);
            } finally {
                if (isCurrent()) submittingRef.current = false;
            }
        },
        [applyFailure, clearTimers, enroll, isSimulated, simConfig, verify, walletAddress, selectedAccount]
    );
    submitRef.current = submitApplication;

    const hasAutostartedRef = useRef(false);
    useEffect(() => {
        if (!simConfig?.autostart || hasAutostartedRef.current) return;
        hasAutostartedRef.current = true;
        if (simConfig.mode === "connect" || simConfig.mode === "full") {
            connectWallet();
            return;
        }
        startEnrollment();
        timersRef.current.push(
            window.setTimeout(
                () => void submitRef.current(DEFAULT_DRAFT),
                AUTOSTART_DELAY_BEFORE_SUBMIT / simConfig.speed
            )
        );
    }, [connectWallet, simConfig, startEnrollment]);

    const fullChainStartedRef = useRef(false);
    useEffect(() => {
        if (!simConfig?.autostart || simConfig.mode !== "full" || !simWallet.connected) return;
        if (fullChainStartedRef.current) return;
        fullChainStartedRef.current = true;
        timersRef.current.push(
            window.setTimeout(() => {
                startEnrollment();
                timersRef.current.push(
                    window.setTimeout(
                        () => void submitRef.current(DEFAULT_DRAFT),
                        AUTOSTART_DELAY_BEFORE_SUBMIT / simConfig.speed
                    )
                );
            }, AUTOSTART_DELAY_AFTER_CONNECT / simConfig.speed)
        );
    }, [simConfig, simWallet.connected, startEnrollment]);

    useEffect(() => {
        if (pageView === "rewards-enabled" || pageView === "account-unavailable") {
            analyticsRef.current?.changed("enroll:result_viewed", {
                result: pageView.replaceAll("-", "_"),
                account_status: currentSuccess ? "active" : unavailableAccountStatus?.toLowerCase(),
                is_rewards_eligible: currentSuccess?.isRewardsEligible
            });
            analyticsRef.current?.close("completed");
        }
    }, [pageView, currentSuccess, unavailableAccountStatus]);

    return {
        analytics: analyticsRef.current,
        pageView,
        formPhase,
        verificationMethod: isSimulated ? simVerificationMethod : verificationMethod,
        signingFallback,
        formFailure,
        successfulAccount: currentSuccess,
        unavailableAccountStatus,
        termsResetKey,
        walletAddress,
        isSquads: !isSimulated && selectedAccount.isSquads,
        vaultAddress: isSimulated ? walletAddress : selectedAccount.vaultAddress,
        isAccountReady: isSimulated || selectedAccount.ready,
        walletProviderName,
        isWalletConnected,
        isWalletConnecting,
        isSimulated,
        simMode: simConfig?.mode ?? null,
        connectWallet,
        startEnrollment,
        cancelEnrollment,
        clearFailure,
        submitApplication,
        isSubmittingApplication: isSimulated ? false : enroll.isLoading
    };
}
